import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { AppShell, InternalReviewShell } from "../components/AppShell";
import { UnitDefinitionNotes } from "../components/ReviewNotes";
import { UnitCard } from "../components/UnitCard";
import { nbaFeedLabFixture } from "../data/generated/nba-feed-lab";
import { consumerUnit } from "../domain/consumer-unit";
import { hrefWithDemoViewer, useDemoViewer, } from "../domain/demo-viewer";
import { isLiveAvailabilityPhase } from "../domain/game-state";
import { labRoutePath } from "../domain/routes";
const catalogue = nbaFeedLabFixture.catalogue;
const featuredContentUnits = [
    {
        category: "Live moments",
        description: "Put the defining play directly in the flow, with smooth in-place playback and a natural route into the full-screen experience.",
        exampleId: "nyk-atl-brunson-bucket-clip",
        kind: "vertical-clip",
        title: "Playable highlights",
    },
    {
        category: "Live moments",
        description: "Use original NBA and partner footage with the context that explains why the moment matters now.",
        exampleId: "nyk-atl-robinson-hustle-source",
        kind: "source-video",
        title: "Source video, in context",
    },
    {
        category: "Voices and personality",
        description: "Bring commentators, creators and former players into the live account with clear attribution and a distinctive point of view.",
        exampleId: "nyk-atl-jesser-final-possession",
        kind: "attributed-commentary",
        title: "Celebrity and creator voices",
    },
    {
        category: "The story of the game",
        description: "Recognise when a connected run changes the shape of the game, rather than reducing it to a list of individual possessions.",
        exampleId: "nyk-atl-q3-run",
        kind: "momentum-swing",
        title: "Momentum and turning points",
    },
    {
        category: "The story of the game",
        description: "Combine trusted game data into an immediate explanation of what is driving the result.",
        exampleId: "por-den-team-shape",
        kind: "game-insight",
        title: "Game insight",
    },
    {
        category: "The story of the game",
        description: "Give a late-arriving fan the shortest useful route into the game, with the key shifts preserved in order.",
        exampleId: "nyk-atl-catch-up",
        kind: "catch-up-summary",
        title: "Catch Up",
    },
    {
        category: "Live utility",
        description: "Elevate the update that genuinely changes what the fan needs to know, without making every score change feel urgent.",
        exampleId: "por-den-regulation-tie",
        kind: "urgent-game-update",
        title: "Urgent game updates",
    },
    {
        category: "Live utility",
        description: "Present a relevant market in the context of the live score and player performance, with the fan always in control.",
        exampleId: "0022501143-contextual-live-odds",
        kind: "contextual-odds",
        title: "Contextual odds",
    },
    {
        category: "Deeper coverage",
        description: "Open a richer collection when one card is not enough, carrying the story into highlights, analysis and postgame coverage.",
        exampleId: "por-den-den-top-plays-story",
        kind: "story-package",
        title: "Deeper story packages",
    },
];
const featuredUnits = featuredContentUnits.map((feature) => {
    const definition = catalogue.find((unit) => unit.kind === feature.kind);
    const example = definition?.examples.find((candidate) => candidate.id === feature.exampleId);
    if (!definition || !example) {
        throw new Error(`Missing featured Content Unit ${feature.kind}/${feature.exampleId}`);
    }
    return {
        ...feature,
        example,
        visual: definition.visual,
    };
});
export function UnitsPage() {
    const categories = [
        ...new Set(featuredUnits.map((unit) => unit.category)),
    ];
    return (_jsx(AppShell, { active: "units", children: _jsxs("article", { className: "content-units-page", children: [_jsxs("header", { className: "content-units-hero", children: [_jsx("p", { className: "section-kicker", children: "NBA Pulse content system" }), _jsx("h1", { children: "A feed that changes form with the game." }), _jsx("p", { children: "Pulse can move from the fastest verified update to rich video, analysis and personality without forcing every moment into the same template. These representative treatments show the range we can shape with the NBA for the opening experience and beyond." }), _jsx("a", { href: "/home/", children: "Explore them together in the Prototype \u2192" })] }), categories.map((category) => (_jsxs("section", { className: "content-units-group", children: [_jsx("h2", { children: category }), _jsx("div", { className: "content-units-grid", children: featuredUnits
                                .filter((unit) => unit.category === category)
                                .map((unit) => (_jsxs("article", { className: "content-unit", children: [_jsx("div", { className: "unit-preview", children: _jsx(UnitCard, { compact: true, isLiveGame: isLiveAvailabilityPhase(unit.example.review
                                                .availabilityPhase), item: consumerUnit(unit.example), visual: unit.visual }) }), _jsxs("div", { className: "content-unit__copy", children: [_jsx("h3", { children: unit.title }), _jsx("p", { children: unit.description })] })] }, `${unit.kind}/${unit.exampleId}`))) })] }, category)))] }) }));
}
export function InternalFeedItemsPage() {
    const { viewer } = useDemoViewer();
    const catalogueGroups = [
        ...new Set(catalogue.map((unit) => unit.group)),
    ];
    const groups = [
        ...catalogueGroups.filter((group) => group !== "Above the feed"),
        ...catalogueGroups.filter((group) => group === "Above the feed"),
    ];
    return (_jsx(InternalReviewShell, { children: _jsxs("section", { className: "catalogue-page", children: [_jsxs("header", { className: "catalogue-page__header", children: [_jsx("p", { className: "section-kicker", children: "Internal review lab" }), _jsx("h1", { children: "Feed Item catalogue" }), _jsx("p", { children: "The exhaustive working catalogue for implementation, content review and QA. This surface is intentionally separate from the NBA client narrative." })] }), groups.map((group) => (_jsxs("section", { className: "catalogue-group", children: [_jsx("h2", { children: group === "Above the feed"
                                ? "Supporting surface"
                                : group }), _jsx("div", { className: "catalogue-grid", children: catalogue
                                .filter((unit) => unit.group === group)
                                .map((unit) => (_jsxs("article", { className: "catalogue-entry", children: [_jsx("div", { className: "unit-preview", children: _jsx(UnitCard, { compact: true, isLiveGame: isLiveAvailabilityPhase(unit.examples[0].review
                                                .availabilityPhase), item: consumerUnit(unit.examples[0]), visual: unit.visual }) }), _jsx(UnitDefinitionNotes, { reviewHref: hrefWithDemoViewer(labRoutePath({
                                            kind: "internal-feed-item",
                                            unitCode: unit.code,
                                        }), viewer), unit: unit })] }, unit.code))) })] }, group)))] }) }));
}
